import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import ZoneSelector from "@/components/ZoneSelector";
import StripeConnectCard from "@/components/StripeConnectCard";
import { 
  ArrowLeft,
  Upload,
  FileText,
  MapPin,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
  Plus,
  X,
  Euro,
  Calendar,
  ChevronRight,
  LogOut,
  Home,
  Building2,
  Camera,
  ChevronDown
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const DAYS = [
  { id: "lundi", label: "Lun" },
  { id: "mardi", label: "Mar" },
  { id: "mercredi", label: "Mer" },
  { id: "jeudi", label: "Jeu" },
  { id: "vendredi", label: "Ven" },
  { id: "samedi", label: "Sam" },
  { id: "dimanche", label: "Dim" }
];

const TIME_SLOTS = [
  { id: "matin", label: "Matin (7h-12h)" },
  { id: "apres_midi", label: "Après-midi (12h-18h)" },
  { id: "soir", label: "Soir (18h-21h)" },
  { id: "nuit", label: "Nuit (21h-7h)" }
];

const SURCHARGE_LABELS = {
  night: "Nuit (21h-7h)",
  weekend: "Weekend / Jours fériés"
};

const NurseOnboarding = () => {
  const { user, token, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const pictureInputRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Form states
  const [documents, setDocuments] = useState({
    rpps_number: "",
    rcp_document: "",
    identity_document: ""
  });
  const [identityFile, setIdentityFile] = useState(null);  // {name, type, data} new upload OR {name, type, storage_path} from server
  const identityFileInputRef = useRef(null);
  const [rcpFile, setRcpFile] = useState(null);  // {name, type, data} new upload OR {name, type, storage_path} from server
  const rcpFileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    zones: [], // Array of {city, postal_code, radius, lat, lng}
    skills: [],
    description: "",
    base_rate: 20,
    night_surcharge: 10,
    weekend_surcharge: 10,
    conventionne: null
  });

  const [businessInfo, setBusinessInfo] = useState({
    business_name: "",
    siret: "",
    billing_address: "",
    billing_postal_code: "",
    billing_city: "",
    tva_number: ""
  });
  const [billingCityOptions, setBillingCityOptions] = useState([]);

  const [availability, setAvailability] = useState({});

  const availableSkills = [];

  const suggestedZones = [
    "Paris 1er", "Paris 2ème", "Paris 3ème", "Paris 4ème", "Paris 5ème",
    "Paris 6ème", "Paris 7ème", "Paris 8ème", "Paris 9ème", "Paris 10ème",
    "Paris 11ème", "Paris 12ème", "Paris 13ème", "Paris 14ème", "Paris 15ème",
    "Paris 16ème", "Paris 17ème", "Paris 18ème", "Paris 19ème", "Paris 20ème",
    "Boulogne-Billancourt", "Neuilly-sur-Seine", "Levallois-Perret", "Vincennes",
    "Saint-Denis", "Montreuil", "Nanterre", "Versailles"
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user?.picture) setProfilePicture(user.picture);
  }, [user]);

  const handlePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 3 Mo");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Format accepté : JPG, PNG, WebP");
      return;
    }

    setUploadingPicture(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result;
        const response = await fetch(`${API}/auth/me/picture`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ picture: base64 })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || "Erreur lors de l'envoi de la photo");
        }
        setProfilePicture(base64);
        await checkAuth();
        toast.success("Photo mise à jour !");
      } catch (error) {
        toast.error(error.message);
      } finally {
        setUploadingPicture(false);
        if (pictureInputRef.current) pictureInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePicture = async () => {
    setUploadingPicture(true);
    try {
      const response = await fetch(`${API}/auth/me/picture`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ picture: null })
      });
      if (!response.ok) throw new Error("Erreur lors de la suppression");
      setProfilePicture(null);
      await checkAuth();
      toast.success("Photo supprimée");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPicture(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/nurses/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        
        // Determine current step based on onboarding_step
        if (data.onboarding_step === 0) {
          setCurrentStep(1); // Documents
        } else if (data.onboarding_step === 1) {
          setCurrentStep(2); // Waiting validation OR can fill profile
        } else {
          setCurrentStep(3); // Verified, full profile edit
        }

        // Populate form data
        setDocuments({
          rpps_number: data.rpps_number || "",
          rcp_document: data.rcp_document || "",
          identity_document: data.identity_document || ""
        });
        if (data.identity_document_file) {
          setIdentityFile(data.identity_document_file);
        }
        if (data.rcp_document_file) {
          setRcpFile(data.rcp_document_file);
        }
        
        // Handle zones - ensure new format with postal_code
        const normalizedZones = (data.zones || []).map(zone => {
          if (typeof zone === 'string') {
            return { city: zone, postal_code: "", radius: 10, lat: null, lng: null };
          }
          return {
            city: zone.city || "",
            postal_code: zone.postal_code || "",
            radius: zone.radius || 10,
            lat: zone.lat || null,
            lng: zone.lng || null
          };
        });
        
        setProfileData({
          zones: normalizedZones,
          skills: data.skills || [],
          description: data.description || "",
          base_rate: data.base_rate || 20,
          night_surcharge: data.night_surcharge || 10,
          weekend_surcharge: data.weekend_surcharge || 10,
          conventionne: data.conventionne ?? null
        });

        // Load business info
        if (data.business_info) {
          setBusinessInfo({
            business_name: data.business_info.business_name || "",
            siret: data.business_info.siret || "",
            billing_address: data.business_info.billing_address || "",
            billing_postal_code: data.business_info.billing_postal_code || "",
            billing_city: data.business_info.billing_city || "",
            tva_number: data.business_info.tva_number || ""
          });
        }

        setAvailability(data.availability || {});
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load all communes for the billing postal code (a postal code can cover several cities)
  useEffect(() => {
    const fetchBillingCities = async () => {
      if (businessInfo.billing_postal_code?.length === 5) {
        try {
          const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${businessInfo.billing_postal_code}&fields=nom`);
          const data = res.ok ? await res.json() : [];
          const cities = (data || []).map((c) => c.nom);
          setBillingCityOptions(cities);
          setBusinessInfo(prev => {
            if (!prev.billing_city || (cities.length > 0 && !cities.includes(prev.billing_city))) {
              return { ...prev, billing_city: cities[0] || prev.billing_city };
            }
            return prev;
          });
        } catch (e) { console.debug("Billing city lookup failed:", e); }
      } else {
        setBillingCityOptions([]);
      }
    };
    fetchBillingCities();
  }, [businessInfo.billing_postal_code]);


  const handleIdentityFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Le fichier ne doit pas dépasser 5 Mo");
      return;
    }
    if (!/^(image\/(jpeg|jpg|png|webp)|application\/pdf)$/i.test(file.type)) {
      toast.error("Format accepté : JPG, PNG, WebP ou PDF");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setIdentityFile({
        name: file.name,
        type: file.type,
        data: reader.result, // base64 data URL, will be uploaded on submit
      });
    };
    reader.readAsDataURL(file);
  };

  const removeIdentityFile = () => {
    setIdentityFile(null);
    if (identityFileInputRef.current) identityFileInputRef.current.value = "";
  };

  const handleRcpFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Le fichier ne doit pas dépasser 5 Mo");
      return;
    }
    if (!/^(image\/(jpeg|jpg|png|webp)|application\/pdf)$/i.test(file.type)) {
      toast.error("Format accepté : JPG, PNG, WebP ou PDF");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setRcpFile({
        name: file.name,
        type: file.type,
        data: reader.result, // base64 data URL, will be uploaded on submit
      });
    };
    reader.readAsDataURL(file);
  };

  const removeRcpFile = () => {
    setRcpFile(null);
    if (rcpFileInputRef.current) rcpFileInputRef.current.value = "";
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    
    if (!documents.rpps_number || !documents.identity_document) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (!rcpFile || (!rcpFile.data && !rcpFile.storage_path)) {
      toast.error("Veuillez joindre votre attestation RCP (image ou PDF)");
      return;
    }
    if (!identityFile || (!identityFile.data && !identityFile.storage_path)) {
      toast.error("Veuillez joindre votre pièce d'identité (image ou PDF)");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...documents,
        rcp_document_file: rcpFile.data
          ? { name: rcpFile.name, type: rcpFile.type, data: rcpFile.data }
          : { name: rcpFile.name, type: rcpFile.type, storage_path: rcpFile.storage_path },
        identity_document_file: identityFile.data
          ? { name: identityFile.name, type: identityFile.type, data: identityFile.data }
          : { name: identityFile.name, type: identityFile.type, storage_path: identityFile.storage_path }
      };
      const response = await fetch(`${API}/nurses/onboarding/documents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Erreur lors de l'envoi");
      }

      toast.success("Documents envoyés !");
      setCurrentStep(2); // Move to profile setup
      fetchProfile();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (profileData.zones.length === 0) {
      toast.error("Veuillez ajouter au moins une zone d'intervention");
      return;
    }
    if (profileData.conventionne === null || profileData.conventionne === undefined) {
      toast.error("Veuillez indiquer votre conventionnement");
      return;
    }

    setSaving(true);

    try {
      // Save profile with business info
      const profileResponse = await fetch(`${API}/nurses/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...profileData,
          business_info: businessInfo
        })
      });

      if (!profileResponse.ok) {
        throw new Error("Erreur lors de la mise à jour du profil");
      }

      // Save availability
      const availResponse = await fetch(`${API}/nurses/availability`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ availability })
      });

      if (!availResponse.ok) {
        throw new Error("Erreur lors de la mise à jour des disponibilités");
      }

      toast.success("Profil enregistré avec succès !");
      
      if (profile?.onboarding_step >= 2) {
        navigate("/nurse/dashboard");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSkill = (skill) => {
    if (profileData.skills.includes(skill)) {
      setProfileData({ ...profileData, skills: profileData.skills.filter(s => s !== skill) });
    } else {
      setProfileData({ ...profileData, skills: [...profileData.skills, skill] });
    }
  };

  const toggleAvailability = (day, slot) => {
    const key = `${day}_${slot}`;
    setAvailability(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      {/* Navigation */}
      <nav className="bg-[#1F2A38] border-b border-[#2A3A4D]">
        <div className="container-ip">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 w-auto object-contain" />
            </Link>
            
            <div className="flex items-center gap-4">
              <span className="text-[#94A3B8] hidden sm:inline">
                {user?.name}
              </span>
              {profile?.onboarding_step >= 2 && (
                <Link to="/nurse/dashboard">
                  <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-white">
                    <Home className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-[#94A3B8] hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="container-ip py-8 max-w-4xl mx-auto">
        {profile?.onboarding_step >= 2 ? (
          <Link to="/nurse/dashboard" className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Link>
        ) : (
          <Link to="/" className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
        )}

        <h1 className="text-2xl font-bold text-white mb-2">
          {profile?.onboarding_step >= 2 ? "Mon profil infirmier" : "Inscription infirmier"}
        </h1>
        <p className="text-[#94A3B8] mb-8">
          {profile?.onboarding_step >= 2 
            ? "Gérez vos informations, zones et disponibilités"
            : "Complétez votre profil pour commencer à recevoir des demandes"
          }
        </p>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { num: 1, label: "Documents", icon: FileText },
            { num: 2, label: "Profil & Zones", icon: MapPin },
            { num: 3, label: "Disponibilités", icon: Calendar }
          ].map((step, index) => (
            <div key={step.num} className="flex items-center">
              <button
                onClick={() => setCurrentStep(step.num)}
                data-testid={`onboarding-step-${step.num}-btn`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentStep === step.num
                    ? "bg-[#00E073] text-[#0D1C32]"
                    : currentStep > step.num || (profile?.onboarding_step >= step.num)
                    ? "bg-[#00E073]/20 text-[#00E073]"
                    : "bg-[#1F2A38] text-[#94A3B8] hover:text-white"
                }`}
              >
                <step.icon className="w-4 h-4" />
                <span className="whitespace-nowrap">{step.label}</span>
              </button>
              {index < 2 && <ChevronRight className="w-4 h-4 text-[#2A3A4D] mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 1: Documents */}
        {currentStep === 1 && (
          <div className="card-dark p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#00E073]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Vérification des documents</h2>
                <p className="text-sm text-[#94A3B8]">Ces informations sont obligatoires pour exercer</p>
              </div>
            </div>

            {profile?.onboarding_step >= 1 ? (
              <div className="bg-[#00E073]/10 border border-[#00E073]/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-[#00E073]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Documents soumis</span>
                </div>
                <p className="text-sm text-[#94A3B8] mt-1">
                  {profile?.onboarding_step === 1 
                    ? "En attente de validation par notre équipe (24-48h)"
                    : "Vos documents ont été validés"
                  }
                </p>
              </div>
            ) : null}

            <form onSubmit={handleDocumentSubmit} className="space-y-5">
              <div>
                <Label className="form-label">Numéro RPPS *</Label>
                <Input
                  value={documents.rpps_number}
                  onChange={(e) => setDocuments({ ...documents, rpps_number: e.target.value })}
                  className="input-dark"
                  placeholder="11 chiffres"
                  required
                  disabled={profile?.onboarding_step >= 1}
                  data-testid="rpps-input"
                />
                <p className="text-xs text-[#64748B] mt-1">
                  Répertoire Partagé des Professionnels de Santé
                </p>
              </div>

              <div>
                <Label className="form-label flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Attestation RCP *
                </Label>
                <p className="text-xs text-[#64748B] mb-2">
                  Attestation de Responsabilité Civile Professionnelle en cours de validité. Formats acceptés : JPG, PNG, WebP, PDF (max 5 Mo).
                </p>

                {rcpFile ? (
                  <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[#00E073] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate" data-testid="rcp-file-name">{rcpFile.name || "Attestation RCP"}</p>
                        <p className="text-xs text-[#64748B]">
                          {rcpFile.storage_path ? "Déjà envoyée" : "Prête à être envoyée"}
                        </p>
                      </div>
                    </div>
                    {profile?.onboarding_step < 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeRcpFile}
                        className="text-[#94A3B8] hover:text-red-400"
                        data-testid="remove-rcp-file-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      ref={rcpFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleRcpFileChange}
                      className="hidden"
                      data-testid="rcp-file-input"
                      disabled={profile?.onboarding_step >= 1}
                    />
                    <Button
                      type="button"
                      onClick={() => rcpFileInputRef.current?.click()}
                      className="btn-secondary w-full"
                      disabled={profile?.onboarding_step >= 1}
                      data-testid="upload-rcp-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Joindre mon attestation RCP
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label className="form-label">Pièce d'identité *</Label>
                <Input
                  value={documents.identity_document}
                  onChange={(e) => setDocuments({ ...documents, identity_document: e.target.value })}
                  className="input-dark"
                  placeholder="Numéro CNI ou passeport"
                  required
                  disabled={profile?.onboarding_step >= 1}
                  data-testid="identity-input"
                />
              </div>

              {/* Upload du justificatif d'identité */}
              <div>
                <Label className="form-label flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Justificatif de pièce d'identité *
                </Label>
                <p className="text-xs text-[#64748B] mb-2">
                  Recto/verso de votre CNI ou passeport. Formats acceptés : JPG, PNG, WebP, PDF (max 5 Mo).
                </p>

                {identityFile ? (
                  <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[#00E073] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate" data-testid="identity-file-name">{identityFile.name || "Pièce d'identité"}</p>
                        <p className="text-xs text-[#64748B]">
                          {identityFile.storage_path ? "Déjà envoyée" : "Prête à être envoyée"}
                        </p>
                      </div>
                    </div>
                    {profile?.onboarding_step < 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeIdentityFile}
                        className="text-[#94A3B8] hover:text-red-400"
                        data-testid="remove-identity-file-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      ref={identityFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleIdentityFileChange}
                      className="hidden"
                      data-testid="identity-file-input"
                      disabled={profile?.onboarding_step >= 1}
                    />
                    <Button
                      type="button"
                      onClick={() => identityFileInputRef.current?.click()}
                      className="btn-secondary w-full"
                      disabled={profile?.onboarding_step >= 1}
                      data-testid="upload-identity-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Joindre ma pièce d'identité
                    </Button>
                  </div>
                )}
              </div>

              {profile?.onboarding_step === 0 && (
                <Button 
                  type="submit" 
                  className="btn-primary w-full mt-6" 
                  disabled={saving}
                  data-testid="submit-documents-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Envoyer pour vérification
                    </>
                  )}
                </Button>
              )}

              {profile?.onboarding_step >= 1 && (
                <Button 
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="btn-primary w-full mt-6"
                >
                  Continuer vers le profil
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </form>

            {/* Stripe Connect bank account setup */}
            <div className="mt-8 pt-6 border-t border-[#2A3A4D]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#28CAD4]/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-[#28CAD4]" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Compte bancaire (paiements)</h3>
                  <p className="text-xs text-[#94A3B8]">Configurez votre RIB via Stripe pour recevoir vos honoraires</p>
                </div>
              </div>
              <StripeConnectCard />
            </div>
          </div>
        )}

        {/* Step 2: Profile & Zones */}
        {currentStep === 2 && (
          <div className="card-dark p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#00E073]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Profil & Zones d'intervention</h2>
                <p className="text-sm text-[#94A3B8]">Définissez où vous souhaitez exercer</p>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); setCurrentStep(3); }} className="space-y-6">
              {/* Zones with postal code verification */}
              <div>
                <Label className="form-label flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Zones d'intervention *
                </Label>
                
                <ZoneSelector
                  zones={profileData.zones}
                  onChange={(newZones) => setProfileData({ ...profileData, zones: newZones })}
                />
              </div>

              {/* Description */}
              <div>
                <Label className="form-label">Présentation (optionnel)</Label>
                <Textarea
                  value={profileData.description}
                  onChange={(e) => setProfileData({ ...profileData, description: e.target.value })}
                  className="input-dark min-h-[80px]"
                  placeholder="Quelques mots sur votre expérience et votre approche..."
                  data-testid="description-input"
                />
              </div>

              {/* Rates */}
              <div>
                <Label className="form-label flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Tarifs indicatifs
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-[#64748B]">Base (€)</Label>
                    <Input
                      type="number"
                      value={profileData.base_rate}
                      onChange={(e) => setProfileData({ ...profileData, base_rate: parseFloat(e.target.value) })}
                      className="input-dark"
                      min="0"
                      step="0.5"
                      data-testid="base-rate-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#64748B]">Nuit (+€)</Label>
                    <Input
                      type="number"
                      value={profileData.night_surcharge}
                      onChange={(e) => setProfileData({ ...profileData, night_surcharge: parseFloat(e.target.value) })}
                      className="input-dark"
                      min="0"
                      step="0.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#64748B]">W-E (+€)</Label>
                    <Input
                      type="number"
                      value={profileData.weekend_surcharge}
                      onChange={(e) => setProfileData({ ...profileData, weekend_surcharge: parseFloat(e.target.value) })}
                      className="input-dark"
                      min="0"
                      step="0.5"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#64748B] mt-2">
                  Grille indicative – vous restez libre de fixer vos honoraires.
                </p>
              </div>

              <div>
                <Label className="form-label">Conventionnement *</Label>
                <p className="text-xs text-[#64748B] mb-3">
                  Les infirmiers <strong>conventionnés</strong> ne sont visibles que par les patients étrangers (non affiliés à l'Assurance Maladie française). Les <strong>non conventionnés</strong> sont visibles par tous les patients.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setProfileData({ ...profileData, conventionne: false })}
                    className={`text-left p-3 rounded-lg border transition-colors ${profileData.conventionne === false ? "border-[#00E073] bg-[#00E073]/10" : "border-[#2A3A4D] bg-[#0D1C32] hover:border-[#00E073]/40"}`}
                    data-testid="onboarding-conventionne-non-btn"
                  >
                    <p className="text-white font-medium text-sm">Infirmier libéral non conventionné</p>
                    <p className="text-xs text-[#64748B] mt-1">Visible par tous les patients</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfileData({ ...profileData, conventionne: true })}
                    className={`text-left p-3 rounded-lg border transition-colors ${profileData.conventionne === true ? "border-[#00E073] bg-[#00E073]/10" : "border-[#2A3A4D] bg-[#0D1C32] hover:border-[#00E073]/40"}`}
                    data-testid="onboarding-conventionne-oui-btn"
                  >
                    <p className="text-white font-medium text-sm">Infirmier libéral conventionné</p>
                    <p className="text-xs text-[#64748B] mt-1">Visible uniquement par les patients étrangers</p>
                  </button>
                </div>
              </div>

              {/* Business Information */}
              <div className="border-t border-[#2A3A4D] pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-[#28CAD4]/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-[#28CAD4]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">Informations entreprise</h3>
                    <p className="text-xs text-[#94A3B8]">Pour la facturation aux patients (optionnel)</p>
                  </div>
                </div>

                {/* Profile picture upload */}
                <div className="mb-6">
                  <Label className="form-label flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Photo de profil
                  </Label>
                  <p className="text-xs text-[#64748B] mb-3">
                    Cette photo apparaîtra en icône dans la liste des infirmiers disponibles vue par les patients.
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-[#1F2A38] border-2 border-[#2A3A4D] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          alt="Photo de profil"
                          className="w-full h-full object-cover"
                          data-testid="profile-picture-preview"
                        />
                      ) : (
                        <Camera className="w-7 h-7 text-[#64748B]" />
                      )}
                    </div>
                    <div className="flex-1 flex flex-wrap gap-2">
                      <input
                        ref={pictureInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePictureChange}
                        className="hidden"
                        data-testid="profile-picture-input"
                      />
                      <Button
                        type="button"
                        onClick={() => pictureInputRef.current?.click()}
                        className="btn-secondary"
                        disabled={uploadingPicture}
                        data-testid="upload-picture-btn"
                      >
                        {uploadingPicture ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {profilePicture ? "Changer la photo" : "Ajouter une photo"}
                      </Button>
                      {profilePicture && (
                        <Button
                          type="button"
                          onClick={handleRemovePicture}
                          variant="ghost"
                          className="text-[#94A3B8] hover:text-red-400"
                          disabled={uploadingPicture}
                          data-testid="remove-picture-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">
                    JPG, PNG ou WebP – Max 3 Mo. De préférence un portrait carré.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="form-label">Raison sociale / Nom</Label>
                    <Input
                      value={businessInfo.business_name}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, business_name: e.target.value })}
                      className="input-dark"
                      placeholder="Ex: Cabinet Infirmier Dupont ou Marie Dupont"
                      data-testid="business-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Numéro SIRET</Label>
                      <Input
                        value={businessInfo.siret}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, siret: e.target.value })}
                        className="input-dark"
                        placeholder="14 chiffres"
                        maxLength={14}
                        data-testid="siret-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">N° TVA (optionnel)</Label>
                      <Input
                        value={businessInfo.tva_number}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, tva_number: e.target.value })}
                        className="input-dark"
                        placeholder="FR + 11 chiffres"
                        data-testid="tva-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Adresse de facturation</Label>
                    <Input
                      value={businessInfo.billing_address}
                      onChange={(e) => setBusinessInfo({ ...businessInfo, billing_address: e.target.value })}
                      className="input-dark"
                      placeholder="123 rue de la Santé"
                      data-testid="billing-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Code postal</Label>
                      <Input
                        value={businessInfo.billing_postal_code}
                        onChange={(e) => setBusinessInfo({ ...businessInfo, billing_postal_code: e.target.value })}
                        className="input-dark"
                        placeholder="75015"
                        maxLength={5}
                        data-testid="billing-postal-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Ville</Label>
                      <div className="relative">
                        <select
                          value={businessInfo.billing_city}
                          onChange={(e) => setBusinessInfo({ ...businessInfo, billing_city: e.target.value })}
                          className="input-dark w-full appearance-none pr-8"
                          data-testid="billing-city-select"
                        >
                          {!businessInfo.billing_city && <option value="">Renseignez le code postal</option>}
                          {Array.from(new Set([businessInfo.billing_city, ...billingCityOptions].filter(Boolean))).map((c) => (
                            <option key={c} value={c} className="bg-[#1F2A38] text-white">{c}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="btn-secondary"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  type="button"
                  onClick={async () => {
                    if (profileData.zones.length === 0) {
                      toast.error("Veuillez ajouter au moins une zone d'intervention");
                      return;
                    }
                    setSaving(true);
                    try {
                      const response = await fetch(`${API}/nurses/profile`, {
                        method: "PUT",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          ...profileData,
                          business_info: businessInfo
                        })
                      });
                      if (!response.ok) throw new Error("Erreur lors de l'enregistrement");
                      toast.success("Profil enregistré !");
                    } catch (error) {
                      toast.error(error.message);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="btn-secondary"
                  disabled={saving || profileData.zones.length === 0}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                </Button>
                <Button 
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={profileData.zones.length === 0}
                >
                  Continuer
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Availability */}
        {currentStep === 3 && (
          <div className="card-dark p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#00E073]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Disponibilités</h2>
                <p className="text-sm text-[#94A3B8]">Indiquez vos créneaux habituels</p>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-6">
              {/* Availability Grid */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left text-xs text-[#64748B] pb-3"></th>
                      {DAYS.map(day => (
                        <th key={day.id} className="text-center text-xs text-[#94A3B8] pb-3 px-1">
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map(slot => (
                      <tr key={slot.id}>
                        <td className="text-xs text-[#94A3B8] pr-3 py-1 whitespace-nowrap">
                          {slot.label}
                        </td>
                        {DAYS.map(day => {
                          const key = `${day.id}_${slot.id}`;
                          const isAvailable = availability[key];
                          return (
                            <td key={day.id} className="px-1 py-1">
                              <button
                                type="button"
                                onClick={() => toggleAvailability(day.id, slot.id)}
                                className={`w-full h-10 rounded-lg text-xs transition-colors ${
                                  isAvailable
                                    ? "bg-[#00E073]/30 text-[#00E073] border border-[#00E073]/50"
                                    : "bg-[#2A3A4D] text-[#64748B] hover:bg-[#00E073]/10"
                                }`}
                              >
                                {isAvailable ? "✓" : ""}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-[#64748B]">
                Cliquez sur les créneaux où vous êtes généralement disponible. Vous pourrez ajuster ponctuellement.
              </p>

              <div className="flex gap-3">
                <Button 
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="btn-secondary"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Enregistrer mon profil
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Status messages */}
        {profile?.onboarding_step === 1 && currentStep !== 1 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-medium">Documents en cours de vérification</p>
              <p className="text-sm text-[#94A3B8]">
                Vous pouvez compléter votre profil en attendant. Votre compte sera activé après validation (24-48h).
              </p>
            </div>
          </div>
        )}

        {/* Legal Notice */}
        <div className="legal-mention mt-6">
          <p className="text-sm">
            <strong>Indépendance :</strong> En tant qu'infirmier, vous exercez en toute indépendance. 
            Infirmiers Privés est une plateforme d'intermédiation et ne vous impose aucune obligation.
          </p>
        </div>
      </main>
    </div>
  );
};

export default NurseOnboarding;
